// Dok 41 – Checklista Bas-U. Kontrollpunkter genom hela utförandet.

export interface BasuChecklistTemplateItem {
  section: string;
  activity: string;
  requirement: string;
  method: string;
  documentRef: string;
  frequency: string;
}

export const BASU_CHECKLIST_SECTIONS = [
  { key: "uppstart", label: "Uppstart och övertagande" },
  { key: "etablering", label: "Etablering och planering" },
  { key: "produktion", label: "Löpande produktion" },
  { key: "uppfoljning", label: "Uppföljning och avvikelser" },
  { key: "avslut", label: "Avslut och överlämning" },
] as const;

export const BASU_CHECKLIST_STATUS_LABELS: Record<string, string> = {
  planerad: "Planerad",
  pagaende: "Pågående",
  klar: "Klar",
  ej_aktuell: "Ej aktuell",
  avvikelse: "Avvikelse",
};

export const BASU_CHECKLIST_TEMPLATE: BasuChecklistTemplateItem[] = [
  {
    section: "uppstart",
    activity: "Skriftligt utseende av Bas-U",
    requirement: "AML 3 kap. 7 b §, AFS 2023:3 kap. 2",
    method: "Kontrollera att byggherren utsett Bas-U skriftligt och att uppgifterna är avgränsade",
    documentRef: "Dok 07",
    frequency: "Vid start",
  },
  {
    section: "uppstart",
    activity: "Övertagande från Bas-P",
    requirement: "AFS 2023:3 kap. 4",
    method: "Ta emot AMP, risksammanställningar, arbetsområdesutredning och öppna frågor",
    documentRef: "Dok 20",
    frequency: "Vid start",
  },
  {
    section: "uppstart",
    activity: "Granskning av arbetsmiljöplanen",
    requirement: "AFS 2023:3 kap. 3",
    method: "Bedöm om AMP är anpassad till valda arbetsmetoder och faktisk etablering",
    documentRef: "Dok 12",
    frequency: "Vid start och vid ändring",
  },
  {
    section: "uppstart",
    activity: "Förhandsanmälan uppdaterad",
    requirement: "AFS 2023:3 kap. 2 § 12",
    method: "Kontrollera att anmälan finns, är aktuell och anslagen på arbetsplatsen",
    documentRef: "Dok 04",
    frequency: "Vid start och vid ändring",
  },
  {
    section: "etablering",
    activity: "APD-plan upprättad",
    requirement: "AFS 2023:13 kap. 5",
    method: "Etablering, transportvägar, upplag, skyddsområden och räddningsvägar redovisade",
    documentRef: "Dok 26",
    frequency: "Vid etablering och per etapp",
  },
  {
    section: "etablering",
    activity: "Skydds- och ordningsregler anpassade",
    requirement: "AFS 2023:3 kap. 4",
    method: "Reglerna anpassade till projektet och kommunicerade till alla på plats",
    documentRef: "Dok 15",
    frequency: "Vid etablering",
  },
  {
    section: "etablering",
    activity: "Plan för skyddsverksamhet",
    requirement: "AFS 2023:1, AFS 2023:3 kap. 4",
    method: "Möten, skyddsronder, skyddsombud och beredskap planerade och tidsatta",
    documentRef: "Dok 42",
    frequency: "Vid etablering",
  },
  {
    section: "etablering",
    activity: "Platsnärvaro överenskommen",
    requirement: "AFS 2023:3 kap. 2 § 17",
    method: "Omfattning av närvaro och kontaktperson överenskommen med byggherren",
    documentRef: "Dok 43",
    frequency: "Vid start",
  },
  {
    section: "etablering",
    activity: "Introduktion och kvittens av AMP",
    requirement: "AFS 2023:3 kap. 4",
    method: "Alla som arbetar på platsen introducerade och kvittens registrerad",
    documentRef: "Dok 14",
    frequency: "Löpande",
  },
  {
    section: "produktion",
    activity: "Granskning av arbetsberedningar",
    requirement: "AFS 2023:3 kap. 4",
    method: "Risker, skyddsåtgärder, kompetens och samordningsbehov granskade före start",
    documentRef: "Dok 23/24",
    frequency: "Före varje riskfyllt moment",
  },
  {
    section: "produktion",
    activity: "Tillfälliga och samverkande konstruktioner",
    requirement: "AFS 2023:13 kap. 5",
    method: "Kompetent person utsedd, handlingar finns, kontroll utförd före användning",
    documentRef: "Dok 44",
    frequency: "Före användning och löpande",
  },
  {
    section: "produktion",
    activity: "Säkerhetsdatablad och kemiska produkter",
    requirement: "AFS 2023:10",
    method: "Register uppdaterat, riskbedömning gjord för hälsofarliga produkter",
    documentRef: "Dok 29",
    frequency: "Löpande",
  },
  {
    section: "produktion",
    activity: "Toolbox talk / arbetsmiljödialog",
    requirement: "AFS 2023:1",
    method: "Kort genomgång av aktuellt riskmoment med deltagarlista",
    documentRef: "Dok 45",
    frequency: "Veckovis",
  },
  {
    section: "produktion",
    activity: "Samordningsmöten",
    requirement: "AFS 2023:3 kap. 4",
    method: "Möten hålls med entreprenörerna, beslut och åtgärder protokollförs",
    documentRef: "Dok 23",
    frequency: "Enligt plan",
  },
  {
    section: "uppfoljning",
    activity: "Skyddsronder",
    requirement: "AFS 2023:1, AFS 2023:3 kap. 4",
    method: "Rond enligt mall, avvikelser med ansvarig och datum",
    documentRef: "Dok 27",
    frequency: "Enligt plan, minst varannan vecka",
  },
  {
    section: "uppfoljning",
    activity: "Tillbud och olyckor",
    requirement: "AML 3 kap. 3 a §",
    method: "Rapporterade, utredda och åtgärdade; AMP uppdaterad vid behov",
    documentRef: "Dok 28",
    frequency: "Vid händelse",
  },
  {
    section: "uppfoljning",
    activity: "Uppföljning av utförandet",
    requirement: "AFS 2023:3 kap. 4",
    method: "Öppna åtgärder följs upp till stängning",
    documentRef: "Dok 21",
    frequency: "Månadsvis",
  },
  {
    section: "uppfoljning",
    activity: "Disciplinära åtgärder vid regelbrott",
    requirement: "Projektets ordningsregler",
    method: "Tillsägelse, skriftlig varning eller avvisning dokumenteras",
    documentRef: "Dok 46",
    frequency: "Vid händelse",
  },
  {
    section: "avslut",
    activity: "Överlämnande skyddsrond",
    requirement: "AFS 2023:3 kap. 4",
    method: "Slutlig rond med kvarstående punkter och mottagare",
    documentRef: "Dok 27",
    frequency: "Vid avslut",
  },
  {
    section: "avslut",
    activity: "Kvarstående bruksrisker redovisade",
    requirement: "AFS 2023:3 kap. 3",
    method: "Risker vid drift och underhåll överlämnade till byggherre/förvaltare",
    documentRef: "Dok 31",
    frequency: "Vid avslut",
  },
  {
    section: "avslut",
    activity: "Arbetsmiljödokumentation överlämnad",
    requirement: "AFS 2023:3 kap. 2",
    method: "Samlad dokumentation kvitterad av byggherren",
    documentRef: "Dok 22/34",
    frequency: "Vid avslut",
  },
];
